import { XCircle } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "randmarcomps";
import React, { useState } from "react";
import InlineSpinner from "./InlineSpinner";

interface Props {
  onConfirmation: () => Promise<void> | void;
  disabled?: boolean;
  buttonTitle?: string;
  text?: string;
  fab?: boolean;
  children?: React.ReactNode;
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  variant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'link';
}

const colorToVariant = (
  c?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
): 'default' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'link' | null | undefined => {
  switch (c) {
    case 'primary':
      return 'default';
    case 'secondary':
      return 'secondary';
    case 'error':
      return 'destructive';
    default:
      return 'secondary';
  }
};

const ButtonWithDialogConfirm: React.FC<Props> = ({
  onConfirmation,
  disabled,
  buttonTitle,
  text,
  fab,
  children,
  color,
  variant,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirmation();
    } finally {
      setOpen(false);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <>
      {children ? (
        <div onClick={() => setOpen(true)}>{children}</div>
      ) : fab ? (
        <Button
          variant="ghost"
          size="icon"
          aria-label={buttonTitle ?? "remove"}
          disabled={disabled}
          title={buttonTitle}
          onClick={() => setOpen(true)}
        >
          <XCircle className="h-5 w-5" />
        </Button>
      ) : (
        <Button
          variant={variant ?? colorToVariant(color)}
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          {buttonTitle || ""}
        </Button>
      )}

      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !loading && handleCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{buttonTitle}</DialogTitle>
            <DialogDescription>
              {text || "Are you sure?"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={loading}>
              {loading ? <InlineSpinner className="mr-2" decorative /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ButtonWithDialogConfirm;
